import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Nav from "../components/Nav.jsx";
import Footer from "../components/Footer.jsx";
import { useI18n } from "../lib/i18n.jsx";
import { useEffect } from "react";

const PL = {
  back: "Strona główna",
  title: "Polityka prywatności",
  updated: "Ostatnia aktualizacja: 7 maja 2026",
  intro:
    "Niniejsza polityka prywatności opisuje, w jaki sposób Scootive Camp przetwarza Twoje dane osobowe podczas korzystania ze strony scootivecamp.pl.",
  sections: [
    {
      title: "1. Administrator danych",
      body:
        "Administratorem danych osobowych jest **AvePark Sp. z o.o.**, z siedzibą przy ul. Krakowiaków 80/98, 02-255 Warszawa, **NIP: 5223215852**, **REGON: 520780366**. Kontakt w sprawach związanych z ochroną danych: **kontakt@avetravel.pl**.",
    },
    {
      title: "2. Jakie dane zbieramy",
      body:
        "• **Newsletter**: imię i adres e-mail (podany dobrowolnie w formularzu)\n• **Rezerwacje**: dane uczestnika i opiekunów (przekazywane w panelu rezerwacyjnym Avetravel.pl — odrębna polityka)\n• **Analityka**: anonimowe statystyki Google Analytics 4 (jeśli wyrazisz zgodę): ID urządzenia, kraj, typ przeglądarki, kliknięcia w przyciski. **Adres IP jest anonimizowany** (last octet wyzerowany).\n• **Cookies techniczne**: niezbędne do działania strony (np. zapamiętanie języka)",
    },
    {
      title: "3. Cele i podstawy prawne",
      body:
        "• Newsletter — Twoja zgoda (art. 6 ust. 1 lit. a RODO). Możesz ją wycofać w każdej chwili klikając „rezygnuję z subskrypcji' w mailu.\n• Świadczenie usług obozowych — wykonanie umowy (art. 6 ust. 1 lit. b RODO).\n• Analityka anonimowa — Twoja zgoda wyrażona w bannerze cookies.\n• Wypełnienie obowiązków prawnych (księgowość, podatki) — art. 6 ust. 1 lit. c RODO.",
    },
    {
      title: "4. Komu przekazujemy dane",
      body:
        "• **Listmonk** (samodzielnie hostowany przez nas) — przechowuje subskrypcje newslettera\n• **Google Ireland Ltd.** — Google Analytics 4 (zanonimizowane), Google Ads (remarketing — TYLKO po Twojej zgodzie marketingowej), Google Reviews API (publiczne dane wizytówki)\n• **Meta Platforms Ireland Ltd.** — Meta Pixel (Facebook/Instagram remarketing — TYLKO po Twojej zgodzie marketingowej)\n• **Avetravel.pl** — system rezerwacji (jeśli dokonasz rezerwacji)\n• **Generali T.U. S.A.** — ubezpieczenia uczestników\nNigdy nie sprzedajemy danych firmom marketingowym.",
    },
    {
      title: "5. Jak długo przechowujemy dane",
      body:
        "• Newsletter — do momentu wycofania zgody\n• Dane rezerwacyjne — 5 lat od zakończenia obozu (przepisy podatkowe / odpowiedzialność cywilna)\n• Anonimowe statystyki — 14 miesięcy (domyślne ustawienie GA4)",
    },
    {
      title: "6. Twoje prawa",
      body:
        "Zgodnie z RODO masz prawo do:\n• dostępu do swoich danych\n• sprostowania błędnych danych\n• usunięcia danych („prawo do bycia zapomnianym”)\n• ograniczenia przetwarzania\n• przenoszenia danych\n• sprzeciwu wobec przetwarzania\n• cofnięcia zgody w dowolnym momencie\n\nWszystkie te prawa zrealizujesz wysyłając maila na **kontakt@avetravel.pl**. Odpowiadamy w ciągu 30 dni. Masz też prawo wnieść skargę do **Prezesa Urzędu Ochrony Danych Osobowych** (uodo.gov.pl).",
    },
    {
      title: "7. Cookies",
      body:
        "Strona używa dwóch rodzajów cookies:\n• **Niezbędnych** (zawsze aktywne) — zapamiętanie języka, sesja\n• **Analitycznych** (Google Analytics 4) — aktywują się TYLKO po wyrażeniu zgody w bannerze\n\nMożesz w każdej chwili zmienić zgodę klikając w link „Ustawienia cookies” w stopce.",
    },
    {
      title: "8. Bezpieczeństwo",
      body:
        "Strona działa wyłącznie po HTTPS. Hasła i dane wrażliwe przechowujemy zgodnie z najlepszymi praktykami branżowymi. Baza rezerwacji jest hostowana w UE.",
    },
    {
      title: "9. Zmiany polityki",
      body:
        "Każda istotna zmiana niniejszej polityki będzie ogłoszona na tej stronie z aktualną datą. Korzystanie ze strony po zmianie oznacza akceptację nowej wersji.",
    },
  ],
};

const EN = {
  back: "Home",
  title: "Privacy Policy",
  updated: "Last updated: May 7, 2026",
  intro:
    "This privacy policy describes how Scootive Camp processes your personal data when you use scootivecamp.pl.",
  sections: [
    {
      title: "1. Data controller",
      body:
        "The data controller is **AvePark Sp. z o.o.**, registered office at Krakowiaków 80/98, 02-255 Warsaw, Poland, **Tax ID (NIP): 5223215852**, **Statistical ID (REGON): 520780366**. For data-protection inquiries: **kontakt@avetravel.pl**.",
    },
    {
      title: "2. What data we collect",
      body:
        "• **Newsletter**: first name and email (voluntarily submitted)\n• **Bookings**: participant and guardian data (handled by Avetravel.pl booking panel — separate policy)\n• **Analytics**: anonymised Google Analytics 4 stats (with your consent): device ID, country, browser, button clicks. **IP is anonymised** (last octet zeroed).\n• **Technical cookies**: required for the site to work (language preference, session)",
    },
    {
      title: "3. Purposes and legal bases",
      body:
        "• Newsletter — your consent (GDPR Art. 6(1)(a)). Withdraw anytime via the unsubscribe link in any email.\n• Camp services — contract performance (Art. 6(1)(b)).\n• Anonymous analytics — your consent in the cookie banner.\n• Legal obligations (accounting, tax) — Art. 6(1)(c).",
    },
    {
      title: "4. Data recipients",
      body:
        "• **Listmonk** (self-hosted by us) — stores newsletter subscriptions\n• **Google Ireland Ltd.** — Google Analytics 4 (anonymised), Google Ads (remarketing — ONLY with your marketing consent), Google Reviews API (public business listing data)\n• **Meta Platforms Ireland Ltd.** — Meta Pixel (Facebook/Instagram remarketing — ONLY with your marketing consent)\n• **Avetravel.pl** — booking system (if you book)\n• **Generali T.U. S.A.** — participant insurance\nWe never sell data to marketing companies.",
    },
    {
      title: "5. Retention",
      body:
        "• Newsletter — until you withdraw consent\n• Booking data — 5 years after the camp ends (tax law / civil liability)\n• Anonymous stats — 14 months (GA4 default)",
    },
    {
      title: "6. Your rights",
      body:
        "Under GDPR you have the right to:\n• access your data\n• rectify inaccurate data\n• erase data (\"right to be forgotten\")\n• restrict processing\n• data portability\n• object to processing\n• withdraw consent anytime\n\nReach out via **kontakt@avetravel.pl** — we reply within 30 days. You can also file a complaint with the **President of the Polish Personal Data Protection Office** (uodo.gov.pl).",
    },
    {
      title: "7. Cookies",
      body:
        "The site uses two cookie categories:\n• **Essential** (always on) — language preference, session\n• **Analytics** (Google Analytics 4) — ONLY active after consent in the banner\n\nYou can change consent anytime via the \"Cookie settings\" link in the footer.",
    },
    {
      title: "8. Security",
      body:
        "The site runs HTTPS-only. Passwords and sensitive data follow industry best practices. Booking database is hosted in the EU.",
    },
    {
      title: "9. Policy changes",
      body:
        "Any significant change will be announced on this page with an updated date. Continued use of the site after changes means you accept the new version.",
    },
  ],
};

const UK = {
  back: "Головна",
  title: "Політика конфіденційності",
  updated: "Останнє оновлення: 7 травня 2026",
  intro:
    "Ця політика конфіденційності описує, як Scootive Camp обробляє твої персональні дані під час користування сайтом scootivecamp.pl.",
  sections: [
    {
      title: "1. Адміністратор даних",
      body:
        "Адміністратором персональних даних є **AvePark Sp. z o.o.**, з юридичною адресою ul. Krakowiaków 80/98, 02-255 Warszawa, Польща, **NIP: 5223215852**, **REGON: 520780366**. Контакт у справах захисту даних: **kontakt@avetravel.pl**.",
    },
    {
      title: "2. Які дані ми збираємо",
      body:
        "• **Розсилка**: ім'я та електронна пошта (за добровільним наданням)\n• **Бронювання**: дані учасника та опікунів (передаються в панелі бронювання Avetravel.pl — окрема політика)\n• **Аналітика**: анонімна статистика Google Analytics 4 (за твоєю згодою): ID пристрою, країна, тип браузера, кліки. **IP-адреса анонімізується** (останній октет обнулено).\n• **Технічні cookies**: необхідні для роботи сайту (наприклад, запам'ятовування мови)",
    },
    {
      title: "3. Цілі та правові підстави",
      body:
        "• Розсилка — твоя згода (ст. 6 п. 1 літ. a GDPR). Можеш відкликати в будь-який момент клікнувши „відписатись' в емейлі.\n• Послуги табору — виконання договору (ст. 6 п. 1 літ. b GDPR).\n• Анонімна аналітика — твоя згода в банері cookies.\n• Виконання правових обов'язків (бухгалтерія, податки) — ст. 6 п. 1 літ. c GDPR.",
    },
    {
      title: "4. Кому передаємо дані",
      body:
        "• **Listmonk** (хостимо самі) — зберігає підписки на розсилку\n• **Google Ireland Ltd.** — Google Analytics 4 (анонімізовано), Google Ads (ремаркетинг — ТІЛЬКИ за маркетинговою згодою), Google Reviews API (публічні дані візитки)\n• **Meta Platforms Ireland Ltd.** — Meta Pixel (Facebook/Instagram ремаркетинг — ТІЛЬКИ за маркетинговою згодою)\n• **Avetravel.pl** — система бронювання (якщо здійсниш бронювання)\n• **Generali T.U. S.A.** — страхування учасників\nМи ніколи не продаємо дані маркетинговим компаніям.",
    },
    {
      title: "5. Як довго зберігаємо дані",
      body:
        "• Розсилка — до моменту відкликання згоди\n• Дані бронювання — 5 років від закінчення табору (податкові норми / цивільна відповідальність)\n• Анонімна статистика — 14 місяців (стандартне налаштування GA4)",
    },
    {
      title: "6. Твої права",
      body:
        "Згідно з GDPR ти маєш право на:\n• доступ до своїх даних\n• виправлення помилкових даних\n• видалення даних („право бути забутим”)\n• обмеження обробки\n• перенесення даних\n• заперечення проти обробки\n• відкликання згоди в будь-який момент\n\nУсі ці права можеш реалізувати, надіславши лист на **kontakt@avetravel.pl**. Відповідаємо протягом 30 днів. Маєш також право подати скаргу до **Голови Управління захисту персональних даних** (uodo.gov.pl).",
    },
    {
      title: "7. Cookies",
      body:
        "Сайт використовує два типи cookies:\n• **Необхідні** (завжди активні) — запам'ятовування мови, сесія\n• **Аналітичні** (Google Analytics 4) — активуються ТІЛЬКИ після згоди в банері\n\nМожеш у будь-який момент змінити згоду, клікнувши на „Налаштування cookies” у футері.",
    },
    {
      title: "8. Безпека",
      body:
        "Сайт працює виключно по HTTPS. Паролі та чутливі дані зберігаємо відповідно до найкращих практик галузі. База бронювань хоститься в ЄС.",
    },
    {
      title: "9. Зміни політики",
      body:
        "Кожна суттєва зміна цієї політики буде оголошена на цій сторінці з актуальною датою. Користування сайтом після зміни означає прийняття нової версії.",
    },
  ],
};

function renderBody(text) {
  // Tiny markdown renderer: **bold** + line breaks + bullet lines
  return text.split("\n").map((line, i) => (
    <p key={i} className={line.startsWith("•") ? "pl-3 mb-1" : "mb-2"}>
      {line.split(/(\*\*[^*]+\*\*)/g).map((part, j) =>
        part.startsWith("**") && part.endsWith("**") ? (
          <strong key={j} className="text-winter">{part.slice(2, -2)}</strong>
        ) : (
          <span key={j}>{part}</span>
        )
      )}
    </p>
  ));
}

export default function PrivacyPolicy() {
  const { lang } = useI18n();
  const T = lang === "pl" ? PL : lang === "uk" ? UK : EN;
  useEffect(() => {
    document.title = `${T.title} – Scootive Camp`;
    window.scrollTo(0, 0);
  }, [T.title]);

  return (
    <div className="bg-cream min-h-screen">
      <Nav />
      <main className="pt-24 md:pt-32 pb-16">
        <div className="max-w-3xl mx-auto px-5 md:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-winter/70 hover:text-summer font-semibold text-sm mb-6">
            <ArrowLeft className="w-4 h-4" /> {T.back}
          </Link>
          <h1
            className="font-display font-bold text-winter leading-tight mb-2"
            style={{ fontSize: "clamp(2rem, 4.5vw, 3rem)" }}
          >
            {T.title}
          </h1>
          <p className="text-winter/55 text-sm mb-8">{T.updated}</p>
          <p className="text-lg text-winter/80 leading-relaxed mb-10">{T.intro}</p>

          <div className="space-y-8">
            {T.sections.map((s, i) => (
              <section key={i} data-testid={`policy-section-${i}`}>
                <h2 className="font-display font-bold text-winter text-xl mb-3">
                  {s.title}
                </h2>
                <div className="text-winter/75 leading-relaxed text-[15px]">
                  {renderBody(s.body)}
                </div>
              </section>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
